// hooks/useRules.js
import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { useAuth } from "utilities/auth";
import { makeApiRequest, apiRoutes } from "constants/apiRoutes";
import { useEffectiveCompanyId } from "hooks/useEffectiveCompanyId";

export function useRules({ params = {}, limit = 50 } = {}) {
  const [data, setData] = useState([]); // full aggregated list
  const [loading, setLoading] = useState(false); // first load
  const [loadingMore, setLoadingMore] = useState(false); // pagination load
  const [error, setError] = useState(null);

  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [meta, setMeta] = useState({ total: 0, live: 0, inactive: 0 });

  const abortRef = useRef(null);

  const { user } = useAuth();
  const companyId = useEffectiveCompanyId();

  // stable key for param changes
  const paramsKey = useMemo(() => {
    return JSON.stringify(
      Object.entries(params || {}).sort(([a], [b]) => a.localeCompare(b))
    );
  }, [params]);

  /**
   * Internal fetch function for both initial load + loadMore
   */
  const fetchPage = useCallback(
    async ({ append = false } = {}) => {
      if (!user?.id || !user?.accessToken || !companyId) return;

      // Clear previous abort if exists
      if (abortRef.current) {
        abortRef.current.abort();
      }
      abortRef.current = new AbortController();

      const isInitialLoad = !append;

      try {
        if (isInitialLoad) {
          setLoading(true);
          setError(null);
        } else {
          setLoadingMore(true);
        }

        const parsedParams = Object.fromEntries(JSON.parse(paramsKey || "[]"));

        const query = {
          ...parsedParams,
          limit,
        };

        if (append && cursor) {
          query.cursor = cursor;
        }

        const url = apiRoutes.getRules(query);

        const { data: res } = await makeApiRequest(
          "get",
          url,
          { id: user.id, accessToken: user.accessToken },
          companyId,
          abortRef.current.signal
        );

        const { data: pageData, nextCursor, hasMore, meta: metaInfo } = res;

        if (isInitialLoad) {
          setData(pageData);
        } else {
          setData((prev) => [...prev, ...pageData]);
        }

        setCursor(nextCursor);
        setHasMore(hasMore);
        setMeta(metaInfo || { total: 0, live: 0, inactive: 0 });
      } catch (err) {
        if (err.name === "AbortError") return;
        console.error("❌ useRules error:", err);
        setError(err);
      } finally {
        if (isInitialLoad) setLoading(false);
        else setLoadingMore(false);
      }
    },
    [paramsKey, user?.id, user?.accessToken, companyId, limit]
  );

  /**
   * Public API functions
   */

  // Abort pending request on unmount
  useEffect(() => {
    return () => {
      if (abortRef.current) {
        abortRef.current.abort();
      }
    };
  }, []);

  // Reset + fetch first page
  const fetchRules = useCallback(() => {
    setCursor(null);
    setHasMore(false);
    setData([]);
    return fetchPage({ append: false });
  }, [fetchPage]);

  // Fetch next page
  const loadMore = useCallback(() => {
    if (hasMore && !loadingMore) {
      return fetchPage({ append: true });
    }
  }, [fetchPage, hasMore, loadingMore]);

  return {
    rules: data,
    meta,
    loading,
    loadingMore,
    error,
    fetchRules,
    loadMore,
    hasMore,
  };
}
